// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/GraphCitationDialog.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { CITATION_CHIP_CLASS } from "./citationChipStyles";

import { MemoryCitationChip } from "../../../../components/memory-citation/MemoryCitationChip";
import { MEMORY_NODE } from "../../../../utils/graphNodes";
import { type LinkableMemory } from "../../../../utils/memoryCitationFields";
import {
  type TicketMessageCitation,
  isGraphCitation,
} from "../../utils/commentSourceUtils";
import {
  GraphCitationDialog,
  graphCitationLabel,
} from "../GraphCitationDialog";

/** A relationship the knowledge graph found, on a review comment.
 *
 * The dialog already exists and the chat panel has used it since graph
 * citations shipped; this is the Studio half that was never wired, so a graph
 * citation on a review comment fell to the inert "Source" fallback. */
export function GraphCitationLink({
  citation,
  memories = [],
}: {
  citation: TicketMessageCitation;
  /**
   * The saved memories this citation carries that name the sentence they
   * support, numbered over the comment (`CommentCitationLinks`). Each draws the
   * same M chip and detail card as in an answer.
   */
  memories?: LinkableMemory[];
}) {
  const { t } = useTranslation("ui");
  if (!isGraphCitation(citation)) return null;
  const label = graphCitationLabel(
    citation,
    t("commentCitationLinks.relatedClaim"),
  );
  const chips = memories.map((memory) => (
    <MemoryCitationChip key={memory.id} memory={memory} />
  ));
  // why no generic chip for a citation that IS a memory: its own node has no relationship to explain, and the M chip says what it is.
  if (chips.length > 0 && citation.graphNodeId.startsWith(MEMORY_NODE)) {
    return <>{chips}</>;
  }

  const link = (
    <GraphCitationDialog
      citation={citation}
      trigger={
        <button
          type="button"
          data-testid={`comment-panel-citation-graph-${citation.graphNodeId}`}
          aria-label={t("commentCitationLinks.graphChipAriaLabel", { label })}
          className={CITATION_CHIP_CLASS}
        >
          <Text as="span" size="1" weight="medium">
            {label}
          </Text>
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      }
    />
  );
  return chips.length > 0 ? (
    <>
      {link}
      {chips}
    </>
  ) : (
    link
  );
}
